import { TitleHeader } from '../../../../../../Common/TitleHeader'
import { Button, FormTable, Input, Typography } from "@fullscreendigital/carrefour-dex-fe";
import { Grid } from '../../../../../../../Common/Grid'
import { URLS } from '../../../../../../constants'
import React, { useEffect, useMemo, useState } from 'react'
import { getValidationSchema } from './helpers'
import { useFormik } from 'formik'
import { useLocation, useNavigate } from 'react-router-dom'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../../../../utils/api'
import { formatErrorMessage } from '../../../../../../../../../utils/formatting'
import { useNotification } from '../../../../../../../../../hooks'
import { useQuery } from 'react-query'
import ImageUpload from '../../../../../../Common/ImageUpload/ImageUpload'
import { DefectReason, Mark } from '../../../../../../../enums'
import { CircularProgress } from '@mui/material'

const DeliveryRandomProductModifica = () => {
  const { showNotification } = useNotification()
  const location = useLocation()
  const queryParams = location.state
  const navigate = useNavigate()
  const markOptions = Object.keys(Mark).map(key => ({ value: key, label: Mark[key] }));
  const defectReasonOptions = Object.keys(DefectReason).map(key => ({ value: key, label: DefectReason[key] }));

  const [marks, setMarks] = useState([])
  const [defectReasons, setDefectReasons] = useState([])
  const [litigation, setLitigation] = useState(null)
  const [showMarks, setShowMarks] = useState(false)
  const [showDefects, setShowDefects] = useState(false)
  const [imageUploaded, setImageUploaded] = useState(false)

  const { data: productQuantityModification, isLoading } = useQuery(
    ['productQuantityModification'],
    async () => {
      const url = `/shop-reception-mobile/integral/product-quantity-modification/${queryParams.productId}/${queryParams.supportId}/${queryParams.truckId}`;
      const fullUrl = queryParams.ticketId ? `${url}?ticketId=${queryParams.ticketId}` : url;

      return await makeCoreGetRequest(fullUrl);
    },
    {
      initialData: [],
      onError: error =>
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        }),
      onSuccess: (data: any) => {
        formik.setValues(data)
      }
    }
  )

  const validationSchema = useMemo(() => getValidationSchema(), [])
  const formik = useFormik({
    enableReinitialize: false,
    validateOnChange: false,
    initialValues: productQuantityModification || {},
    validationSchema: validationSchema,
    onSubmit: async (values: any) => {
      try {
        const mark = markOptions.find(option => option.label === values.mark)
        const defectReason = mark && mark.value !== '' ?
          defectReasonOptions.find(option => option.label === values.defectReason) : null

        const payload = {
          id: values.productId,
          name: values.productName,
          code: values.supportCode,
          ean: values.productEan,
          eanReceived: null,
          unit: null,
          integral: values.conformQuantity,
          broken: values.defectQuantity,
          quantity: queryParams.quantity,
          shortDlc: null,
          dlc: null,
          defectReason: defectReason?.value || null,
          mark: mark?.value || null,
          shopReceptionDeviation: values.shopReceptionDeviation,
          supportId: values.supportId
        }

        makeCorePostRequest(`/shop-reception-mobile/aleatory/product/save-new-quantities`, payload).then(goBack)
      } catch (error) {
        showNotification({
          message: `Error while submitting. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  })

  const goBack = async () => {
    const { truckId, supportCode, productEan } = formik.values;

    const flowConfig = {
      integral: {
        request: () => makeCoreGetRequest(`/shop-reception-mobile/integral/support/scan/${supportCode}?truckId=${truckId}&ticketId=${queryParams.ticketId}`),
        navigateTo: URLS.AUTH_DELIVERY_COMPLETE_SCAN_STEP_2,
      },
      aleatory_product: {
        request: () => makeCorePostRequest(`/shop-reception-mobile/aleatory/support/scan/${truckId}`, { supportCode }, {
          headers: { 'Content-Type': 'application/json' },
        }),
        navigateTo: URLS.AUTH_DELIVERY_RANDOM_PRODUCT_FINAL,
      },
      aleatory_support: {
        request: () => makeCorePostRequest(`/shop-reception-mobile/aleatory/support/scan/${truckId}`, { supportCode }, {
          headers: { 'Content-Type': 'application/json' },
        }),
        navigateTo: URLS.AUTH_DELIVERY_RANDOM_SUPPORT_FINAL,
      },
    };

    const handleRequest = async ({ request, navigateTo }) => {
      try {
        const response = await request();

        queryParams.flow === 'integral' ?
          navigate(navigateTo, {
            state: { truckId, ticketId: queryParams.ticketId, productEan, data: response },
          }) :
          navigate(navigateTo, {
            state: { ...response, truckId, ticketId: queryParams.ticketId, productEan },
          });
      } catch (error) {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error',
        });
      }
    };

    const flowHandler = flowConfig[queryParams.flow];

    if (flowHandler) {
      await handleRequest(flowHandler);
    } else {
      console.warn('Unknown flow type:', queryParams.flow);
    }

    setShowMarks(false);
    setShowDefects(false);
  };

  useEffect(() => {
    calculateLitigationType()
  }, [formik.values.conformQuantity, formik.values.defectQuantity])

  const calculateLitigationType = () => {
    const currentConformValue = parseFloat(formik.values.conformQuantity)
    const currentDefectValue = parseFloat(formik.values.defectQuantity)
    const currentTotalValue = currentConformValue + currentDefectValue

    if (
      queryParams?.quantity > currentConformValue &&
      queryParams?.quantity === currentTotalValue
    ) {
      setMarks([
        Mark.VEGETABLE_FRUIT_QUALITY,
        Mark.EXPIRED,
        Mark.SHORT_TERM,
        Mark.DAMAGED,
        Mark.VEGETABLES_FRUITS,
        Mark.DETERIORATION_A
      ])

      setLitigation(2)
    } else if (
      queryParams?.quantity > currentConformValue &&
      queryParams?.quantity !== currentTotalValue
    ) {
      setMarks([
        Mark.PALLETS_MERCHANDISE_MISSING,
        Mark.MISSING_CARRIER,
        Mark.QUANTITATIVE_DIFFERENCE
      ])

      setLitigation(1)
    } else if (queryParams?.quantity < currentConformValue) {
      setMarks([Mark.PALLETS_MERCHANDISE_MISSING, Mark.QUANTITATIVE_DIFFERENCE])

      setLitigation(1)
    } else if (queryParams?.quantity === currentConformValue) {
      setLitigation(null)
      setShowMarks(false)
      setShowDefects(false)
    }

    formik.setFieldValue('mark', '')
    formik.setFieldValue('defectReason', '')
  }

  const populateDefectReasons = selectedMark => {
    if (formik.values.mark) {
      formik.setFieldValue('defectReason', '')

      switch (litigation) {
        case 1:
          setDefectReasons([DefectReason.PROVIDER])
          formik.setFieldValue('defectReason', DefectReason.PROVIDER)
          break
        case 2:
          if (selectedMark === Mark.EXPIRED || selectedMark === Mark.SHORT_TERM) {
            setDefectReasons([DefectReason.PROVIDER])
            formik.setFieldValue('defectReason', DefectReason.PROVIDER)
          } else if (selectedMark === Mark.VEGETABLE_FRUIT_QUALITY) {
            setDefectReasons([DefectReason.PROVIDER, DefectReason.SHOP])
          } else if (selectedMark === Mark.DAMAGED) {
            setDefectReasons([DefectReason.PROVIDER, DefectReason.TRANSPORTER, DefectReason.SHOP])
          } else if (
            selectedMark === Mark.VEGETABLES_FRUITS ||
            selectedMark === Mark.DETERIORATION_A
          ) {
            setDefectReasons([DefectReason.PROVIDER, DefectReason.TRANSPORTER])
          }
          break
        default:
          setDefectReasons([])
      }
    }
  }

  useEffect(() => {
    populateDefectReasons(formik.values.mark)
  }, [formik.values.mark])

  const handleSuccess = () => {
    setImageUploaded(true);

    showNotification({
      message: 'Imaginea a fost încărcată cu succes.',
      type: 'success'
    })
  }

  const handleError = error => {
    showNotification({
      message: `${formatErrorMessage(error)}`,
      type: 'error'
    })
  }

  const columns = [
    {
      title: 'Produs',
      field: 'product',
      align: 'left',
      render: (rowData) => (
        <span>
          {rowData.productName}
          <br />
          {rowData.productEans}
        </span>
      )
    },
    {
      title: "Calitativ",
      field: "qualitative",
      align: "left",
      render: (rowData: any) => (
        <Input
          type="number"
          value={rowData.qualitative}
          onChange={e => {
            formik.setFieldValue('conformQuantity', (e.target as HTMLInputElement).value)
            setShowMarks(true)
          }}
          variant="standard"
        />
      )
    },
    {
      title: 'Defect',
      field: 'defective',
      align: 'left',
      render: (rowData: any) => (
        <div style={{ display: 'flex' }}>
          <Input
            type="number"
            value={rowData.defective}
            onChange={e => {
              formik.setFieldValue('defectQuantity', (e.target as HTMLInputElement).value)
              setShowMarks(true)
            }}
            variant="standard"
          />
          {litigation === 2 && (
            <ImageUpload
              uploadUrl={`/shop-reception-mobile/integral/upload/${queryParams.supportId}/${queryParams.productId}`}
              onSuccess={() => handleSuccess()}
              onError={handleError}
            />
          )}
        </div>
      )
    }
  ]

  const rows = [
    {
      productName: formik.values.productName,
      productEans: formik.values.productEans,
      qualitative: formik.values.conformQuantity,
      defective: formik.values.defectQuantity
    }
  ]

  if (isLoading) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  return (
    <Grid
      gridContainerStyle={{ marginTop: '20px' }}
      maxWidth="xs"
      items={[
        <TitleHeader title="Modifică cantităţi" />,
        <Grid
          spacing={1}
          hasPaper={true}
          items={[
            <Typography variant="medium" component="p">
              Suma cantităţilor introduse anterior
            </Typography>
          ]}
          itemsPerRow={1}
        />,
        <Grid
          spacing={1}
          hasPaper={true}
          items={[
            <Typography variant="medium" component="p">
              Număr înmatriculare: {productQuantityModification?.truckNumber}
            </Typography>,
            formik.values?.ticketNumber && (
              <Typography variant="medium" component="p">
                {' '}
                BL: {formik.values?.ticketNumber}{' '}
              </Typography>
            ),
            <Typography variant="medium" component="p">
              Etichetă suport: {productQuantityModification?.supportCode}
            </Typography>
          ]}
          itemsPerRow={1}
        />,
        <FormTable
          columns={columns}
          rows={rows}
        />,
        <>
          { showMarks &&
            <Input
              type="select"
              label="Marca"
              variant="outlined"
              width="100%"
              value={formik.values.mark}
              values={marks?.map(mark => ({ value: mark, label: mark }))}
              onChange={event => {
                formik.setFieldValue('mark', (event.target as HTMLInputElement).value)
                setShowDefects(true)
              }}
              error={!!formik.errors.mark}
              helperText={formik.errors.mark as string}
            />
          }
          { showDefects &&
            <Input
              type="select"
              label="Motiv"
              variant="outlined"
              width="100%"
              value={formik.values.defectReason}
              values={defectReasons?.map(defect => ({ value: defect, label: defect }))}
              onChange={event => {
                formik.setFieldValue('defectReason', (event.target as HTMLInputElement).value)
              }}
              disabled={formik.values.mark === ''}
              error={!!formik.errors.defectReason}
              helperText={formik.errors.defectReason as string}
            />
          }
        </>,
        <Button
          type="button"
          variant="contained"
          label="Salvează"
          onClick={() => formik.handleSubmit()}
          style={{ width: '100%' }}
          disabled={(litigation === 2 && (!imageUploaded || formik.values.mark === "" || formik.values.defectReason === ""))
            || (litigation === 1 && (formik.values.mark === "" || formik.values.defectReason === "")) || formik.isSubmitting}
        />,
        <Button
          type="button"
          variant="contained"
          label="Anulează"
          onClick={() => goBack()}
          style={{ width: '100%' }}
        />
      ]}
      itemsPerRow={1}
    />
  )
}
export default DeliveryRandomProductModifica
